/** Resampling, smoothing, grades, climbs (SPEC §8.1 steps 3, 5, 6). Pure functions on metric LV95 data. */
import type { Climb, ProcessedRoute } from './model';

export interface PlanarPoint {
  e: number;
  n: number;
  ele?: number;
}

export interface Resampled {
  d: number[];
  e: number[];
  n: number[];
  /** Interpolated source elevation (NaN where the source had none). */
  ele: number[];
}

export const RESAMPLE_M = 10;
/** Start and finish closer than this: a loop or an out-and-back. */
export const LOOP_THRESHOLD_M = 100;

export function dist2(a: PlanarPoint, b: PlanarPoint): number {
  return Math.hypot(b.e - a.e, b.n - a.n);
}

/** Direction of the first (dir = 1) or last (dir = −1) ≈ `overM` metres, pointing in the direction of travel. */
function endDirection(pts: PlanarPoint[], dir: 1 | -1, overM = 50): [number, number] | null {
  const i0 = dir === 1 ? 0 : pts.length - 1;
  const p = pts[i0]!;
  let q = p;
  for (let k = 1; k < pts.length; k++) {
    q = pts[i0 + dir * k]!;
    if (dist2(p, q) >= overM) break;
  }
  const de = (q.e - p.e) * dir;
  const dn = (q.n - p.n) * dir;
  const len = Math.hypot(de, dn);
  return len > 1 ? [de / len, dn / len] : null;
}

/**
 * A loop ends where it started *and* rides on in the starting direction. An out-and-back (Nods – Chasseral – Nods)
 * also ends at its start, but arrives from the opposite direction: riding on would be a U-turn, so it is ridden
 * once and finishes at the start.
 */
export function closesAsLoop(pts: PlanarPoint[], thresholdM = LOOP_THRESHOLD_M): boolean {
  if (pts.length < 3 || dist2(pts[0]!, pts[pts.length - 1]!) > thresholdM) return false;
  const out = endDirection(pts, 1);
  const arrive = endDirection(pts, -1);
  if (!out || !arrive) return true;
  return out[0] * arrive[0] + out[1] * arrive[1] > -0.5;
}

export type RouteKind = 'loop' | 'outAndBack' | 'oneWay';

/** Loop (laps), out-and-back (finishes where it started, e.g. up a pass and back down) or one-way (A → B). */
export function routeKind(route: ProcessedRoute): RouteKind {
  if (route.meta.loop) return 'loop';
  const a = route.points[0];
  const b = route.points[route.points.length - 1];
  return a && b && Math.hypot(a.x - b.x, a.z - b.z) <= LOOP_THRESHOLD_M ? 'outAndBack' : 'oneWay';
}

/** Drop consecutive points closer than `minM`. */
export function dedupe(pts: PlanarPoint[], minM = 0.5): PlanarPoint[] {
  const out: PlanarPoint[] = [];
  for (const p of pts) {
    const last = out[out.length - 1];
    if (!last || dist2(last, p) >= minM) out.push(p);
  }
  return out;
}

/**
 * Resample a polyline uniformly at ≈ `target` m (spacing = length / round(length / target)).
 * Uniform spacing keeps smoothing and grade windows exact. For loops the closing point (= start) is dropped,
 * so the arrays are periodic and the route length is `count × step`.
 */
export function resample(
  ptsIn: PlanarPoint[],
  target = RESAMPLE_M,
  loop = false,
): Resampled & { step: number; total: number } {
  const pts = dedupe(ptsIn);
  if (loop && pts.length > 2 && dist2(pts[0]!, pts[pts.length - 1]!) > 0.5) pts.push({ ...pts[0]! });
  const cum: number[] = [0];
  for (let i = 1; i < pts.length; i++) cum.push(cum[i - 1]! + dist2(pts[i - 1]!, pts[i]!));
  const total = cum[cum.length - 1]!;
  const count = Math.max(1, Math.round(total / target));
  const step = total / count;
  const out: Resampled = { d: [], e: [], n: [], ele: [] };
  let j = 0;
  const last = loop ? count - 1 : count;
  for (let k = 0; k <= last; k++) {
    const d = k === count ? total : k * step;
    while (j < pts.length - 2 && cum[j + 1]! < d) j++;
    const a = pts[j]!;
    const b = pts[j + 1] ?? a;
    const seg = cum[j + 1]! - cum[j]! || 1;
    const t = Math.max(0, Math.min(1, (d - cum[j]!) / seg));
    out.d.push(d);
    out.e.push(a.e + (b.e - a.e) * t);
    out.n.push(a.n + (b.n - a.n) * t);
    const ea = a.ele ?? NaN;
    const eb = b.ele ?? NaN;
    out.ele.push(
      Number.isFinite(ea) && Number.isFinite(eb) ? ea + (eb - ea) * t : Number.isFinite(ea) ? ea : eb,
    );
  }
  return { ...out, step, total };
}

function idx(i: number, len: number, loop: boolean): number {
  if (loop) return ((i % len) + len) % len;
  return Math.max(0, Math.min(len - 1, i));
}

export function medianFilter(xs: number[], window = 5, loop = false): number[] {
  const h = Math.floor(window / 2);
  return xs.map((_, i) => {
    const w: number[] = [];
    for (let k = -h; k <= h; k++) w.push(xs[idx(i + k, xs.length, loop)]!);
    w.sort((a, b) => a - b);
    return w[h]!;
  });
}

export function gaussianFilter(xs: number[], sigmaSamples: number, loop = false): number[] {
  const r = Math.ceil(3 * sigmaSamples);
  const kernel: number[] = [];
  let sum = 0;
  for (let k = -r; k <= r; k++) {
    const v = Math.exp(-(k * k) / (2 * sigmaSamples * sigmaSamples));
    kernel.push(v);
    sum += v;
  }
  return xs.map((_, i) => {
    let acc = 0;
    for (let k = -r; k <= r; k++) acc += xs[idx(i + k, xs.length, loop)]! * kernel[k + r]!;
    return acc / sum;
  });
}

/** Median(5) then Gaussian σ ≈ 30 m (SPEC §8.1 step 5). */
export function smoothElevation(ele: number[], step = RESAMPLE_M, loop = false, sigmaM = 30): number[] {
  return gaussianFilter(medianFilter(ele, 5, loop), sigmaM / step, loop);
}

/** Linear interpolation of h at distance d on a uniformly spaced array (wraps for loops). */
function hAt(h: number[], d: number, step: number, loop: boolean, total: number): number {
  let x = d;
  if (loop) x = ((x % total) + total) % total;
  else x = Math.max(0, Math.min(total, x));
  const f = x / step;
  const i = Math.floor(f);
  const a = h[idx(i, h.length, loop)]!;
  const b = h[idx(i + 1, h.length, loop)]!;
  return a + (b - a) * (f - i);
}

/** Grade (%) over a ±25 m window, clamped to ±25 % (SPEC §8.1 step 5). */
export function computeGrades(
  h: number[],
  step = RESAMPLE_M,
  loop = false,
  halfWindowM = 25,
  clampPct = 25,
): number[] {
  const total = (h.length - (loop ? 0 : 1)) * step;
  return h.map((_, i) => {
    const d = i * step;
    let a = d - halfWindowM;
    let b = d + halfWindowM;
    if (!loop) {
      a = Math.max(0, a);
      b = Math.min(total, b);
    }
    if (b - a < 1) return 0;
    const g = ((hAt(h, b, step, loop, total) - hAt(h, a, step, loop, total)) / (b - a)) * 100;
    return Math.max(-clampPct, Math.min(clampPct, g));
  });
}

export function totalAscent(h: number[]): number {
  let up = 0;
  for (let i = 1; i < h.length; i++) up += Math.max(0, h[i]! - h[i - 1]!);
  return up;
}

/**
 * Climbs (SPEC §8.1 step 6): sections ≥ 500 m long with ≥ 3 % average grade.
 * Uses a 200 m smoothed grade with hysteresis to find candidate runs, merges short dips, then validates
 * length and average grade from the actual elevations.
 */
export function detectClimbs(d: number[], h: number[], minLengthM = 500, minAvgPct = 3): Climb[] {
  if (h.length < 3) return [];
  const step = d[1]! - d[0]!;
  const g200 = computeGrades(gaussianFilter(h, 50 / step), step, false, 100, 40);
  const runs: [number, number][] = [];
  let start = -1;
  for (let i = 0; i < g200.length; i++) {
    const up = g200[i]! > 2;
    if (up && start < 0) start = i;
    if (!up && start >= 0) {
      runs.push([start, i - 1]);
      start = -1;
    }
  }
  if (start >= 0) runs.push([start, g200.length - 1]);
  // merge runs separated by < 150 m
  const merged: [number, number][] = [];
  for (const r of runs) {
    const last = merged[merged.length - 1];
    if (last && (r[0] - last[1]) * step < 150) last[1] = r[1];
    else merged.push([r[0], r[1]]);
  }
  const climbs: Climb[] = [];
  for (const [a, b] of merged) {
    const lengthM = d[b]! - d[a]!;
    if (lengthM < minLengthM) continue;
    const gain = h[b]! - h[a]!;
    const avg = (gain / lengthM) * 100;
    if (avg < minAvgPct) continue;
    climbs.push({ startD: d[a]!, endD: d[b]!, lengthM, avgGradePct: avg, gainM: gain });
  }
  return climbs;
}

/** Deterministic 32-bit hash → hex (FNV-1a), used when WebCrypto is unavailable. */
export function fnv1a(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

export async function sha256Hex(s: string): Promise<string> {
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(s));
    return Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, '0')).join('');
  }
  return fnv1a(s);
}
